import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import BackButton from '@/components/BackButton';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ClipboardText, CheckCircle, Clock, Lock } from '@phosphor-icons/react';
import { useLanguage } from '@/contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = '/branding/logo.png';

export default function MySurveys() {
  const { language, direction } = useLanguage();
  const isAr = language === 'ar';
  const T = (ar, en) => (isAr ? ar : en);
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/my/surveys`, { withCredentials: true });
        setList(r.data || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || T('فشل التحميل', 'Failed'));
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="p-8 text-center">{T('جار التحميل...', 'Loading...')}</div>;

  return (
    <div className="min-h-screen bg-[#F2F0FA] dark:bg-slate-950" dir={direction} data-testid="my-surveys-page">
      <header className="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] dark:border-slate-700 py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#655dc6] dark:text-violet-300 flex items-center gap-2">
            <ClipboardText size={28} weight="duotone" />
            {T('استبياناتي', 'My Surveys')}
          </h1>
          <p className="text-sm text-[#655dc6]/70 mt-1">
            {T('استبيانات من إدارة الجودة بانتظار إجابتك', 'Quality surveys awaiting your response')}
          </p>
        </div>

      {list.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-[#655dc6]/60">
            <ClipboardText size={48} className="mx-auto mb-3 opacity-40" />
            <p>{T('لا توجد استبيانات حالياً', 'No surveys at the moment')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {list.map((s) => (
            <Card key={s.id} data-testid={`my-survey-${s.id}`} className={s.already_responded ? 'opacity-70' : ''}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <ClipboardText size={20} className="text-[#655dc6]" />
                      <h3 className="font-semibold text-[#655dc6] dark:text-violet-300 truncate">
                        {isAr ? s.title_ar : (s.title_en || s.title_ar)}
                      </h3>
                      {s.anonymous && (
                        <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">
                          <Lock size={10} className="me-0.5" /> {T('مجهول', 'Anonymous')}
                        </Badge>
                      )}
                      {s.already_responded && (
                        <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">
                          <CheckCircle size={10} className="me-0.5" /> {T('تم الرد', 'Submitted')}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-[#655dc6]/60 mt-1">
                      {isAr ? s.description_ar : s.description_en}
                    </p>
                    <div className="flex gap-3 text-xs text-[#655dc6]/70 mt-2">
                      <span>{T('أسئلة', 'Questions')}: <strong>{s.questions?.length || 0}</strong></span>
                      <span className="flex items-center gap-1">
                        <Clock size={12} /> {T('2-3 دقائق', '2-3 min')}
                      </span>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate(`/surveys/${s.id}`)}
                    disabled={s.already_responded}
                    className="bg-[#655dc6] hover:bg-[#564db5] text-white"
                    data-testid={`fill-${s.id}`}
                  >
                    {s.already_responded ? T('تم الرد', 'Done') : T('ابدأ', 'Start')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </main>
    </div>
  );
}
